import { T } from "@classdojo/web";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import * as React from "react";
import FullScreenCardPage from "./FullScreenCardPage";
import SlideNavigation from "./SlideNavigation";
import SmallCapsHeading from "./SmallCapsHeading";
import WithTeacherTips from "./WithTeacherTips";
import { RemoteSlideProps, Slide } from "./types";
import ShadowCard from "src/shared/components/ShadowCard";

interface Option {
  option: string;
  icon?: string;
  twist?: { twist: string; showTwist: boolean };
  description?: string;
}
export const OptionsSlide = (
  question: React.ReactNode,
  options: Option[],
  twists?: Option[],
  theSentenceStarter?: string,
): Slide => {
  return {
    remote: ({ next, prev }: RemoteSlideProps) => {
      return (
        <WithTeacherTips>
          <div sx={{ padding: "dt_s" }}>
            <QuestionWithOptionsRemote question={question} options={options} twists={twists} next={next} prev={prev} />
            <SlideNavigation
              onBack={() => next(-1)}
              onNext={() => {
                twists && twists.length > 0 ? next(2) : next(getNextCount(options));
              }}
            />
          </div>
        </WithTeacherTips>
      );
    },
    display: () => {
      return <QuestionWithOptionsDisplay question={question} sentenceStarter={theSentenceStarter} options={options} />;
    },
  };
};

function getNextCount(options: Option[]): number {
  let count = 1;
  let twistCount = 0;
  for (const option of options) {
    if (option.twist) {
      if (!option.twist.showTwist) {
        count += Math.pow(2, twistCount);
      }
      twistCount++;
    }
  }
  return count;
}
const QuestionWithOptionsRemote: React.FC<{
  question: React.ReactNode;
  options: Option[];
  twists?: Option[];
  next: () => void;
  prev: () => void;
}> = ({ question, options, twists, next, prev }) => {
  return (
    <span>
      <ShadowCard sx={{ padding: "dt_m" }}>
        <div sx={{ fontSize: "24px", fontWeight: 600, textAlign: "center" }}>{question}</div>
        {options.map((option, index) => (
          <div key={option.option} sx={{ padding: "dt_s", display: "flex", alignItems: "center", gap: "dt_s" }}>
            {option.icon && (
              <div
                sx={{ width: 50, height: 50, background: `url(${option.icon}) center center / contain no-repeat` }}
              />
            )}
            <div sx={{ fontSize: 24, fontWeight: 800, flexGrow: 1 }}>
              <OptionTextRemote
                option={option}
                next={next.bind(null, Math.pow(2, index))}
                prev={prev.bind(null, Math.pow(2, index))}
              />
            </div>
          </div>
        ))}
      </ShadowCard>

      {twists && twists.length && (
        <div>
          <div sx={{ textAlign: "center", padding: "dt_s" }}>
            <SmallCapsHeading>
              <T str="dojo.toolkit:options_slide.add_twist_optional" fallback="Add twist (optional)" />
            </SmallCapsHeading>
          </div>
          {twists.map(({ option, icon, description }) => (
            <ShadowCard
              key={option}
              sx={{ display: "flex", alignItems: "center", gap: "dt_s", padding: "dt_m" }}
              onClick={() => next()}
            >
              {icon && (
                <div
                  sx={{
                    width: 50,
                    height: 50,
                    background: `url(${icon}) center center / contain`,
                  }}
                />
              )}
              <div sx={{ flexGrow: 1 }}>
                <div sx={{ fontSize: 24, fontWeight: 800 }}>{option}</div>
                <div>{description}</div>
              </div>
              <div sx={{ fontSize: "32px", color: "dt_content_tertiary" }}>{`\uff0b`}</div>
            </ShadowCard>
          ))}
        </div>
      )}
    </span>
  );
};

const QuestionWithOptionsDisplay: React.FC<{
  question: React.ReactNode;
  options: Option[];
  sentenceStarter?: string;
}> = ({ question, options, sentenceStarter }) => {
  return (
    <FullScreenCardPage>
      <div sx={{ fontSize: "48px", fontWeight: 600, textAlign: "center", margin: RAW_cssValue("72px 100px 42px") }}>
        {question}
      </div>
      {sentenceStarter && (
        <div sx={{ fontSize: "32px", textAlign: "center", margin: RAW_cssValue("42px") }}>{sentenceStarter}</div>
      )}
      <div sx={{ display: "flex", justifyContent: "center", gap: "dt_m" }}>
        {options.map(({ option, icon, twist, description }) => (
          <ShadowCard key={option} sx={{ padding: "dt_xl", width: 325 }}>
            <div sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "dt_m" }}>
              {icon && (
                <div
                  sx={{
                    width: 150,
                    height: 150,
                    background: `url(${icon}) center center / contain no-repeat`,
                  }}
                />
              )}
              <div sx={{ textAlign: "center" }}>
                <div sx={{ fontSize: 32, fontWeight: 800 }}>{option}</div>
                {description && (
                  <div sx={{ textAlign: "center", fontSize: 24, margin: RAW_cssValue("10px") }}>{description} </div>
                )}

                {twist && twist.showTwist && <div sx={{ fontSize: 24 }}> {twist.twist}</div>}
              </div>
            </div>
          </ShadowCard>
        ))}
      </div>
    </FullScreenCardPage>
  );
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
class OptionTextRemote extends React.Component<{
  option: Option;
  next: (num?: number) => void;
  prev: (num?: number) => void;
}> {
  render() {
    const { option, description, twist } = this.props.option;
    return (
      <div>
        {option}
        {description && <div>{description}</div>}

        {twist &&
          (twist.showTwist ? (
            <>
              <a sx={linkTextStyle} onClick={this.onLinkClicked} data-name="options_slide:hide_twist">
                Hide Twist
              </a>
              <div>
                <div sx={{ color: "dt_content_tertiary" }}>Twist:</div>
                <div sx={twistTextStyle}>{twist.twist}</div>
              </div>
            </>
          ) : (
            <>
              <a sx={linkTextStyle} onClick={this.onLinkClicked} data-name="options_slide:show_twist">
                Show Twist
              </a>
              <div>
                <div sx={{ color: "dt_content_tertiary" }}>Hidden twist:</div>
                <div sx={hiddenTwistTextStyle}>{twist.twist}</div>
              </div>
            </>
          ))}
      </div>
    );
  }

  onLinkClicked = () => {
    this.props.option.twist && this.props.option.twist.showTwist ? this.props.prev() : this.props.next();
  };
}

const linkTextStyle: ThemeUIStyleObject = {
  textAlign: "right",
  float: "right",
  color: "dt_content_tertiary",
};

const twistTextStyle: ThemeUIStyleObject = {};

const hiddenTwistTextStyle: ThemeUIStyleObject = {
  // fontStyle: "italic",
  color: "dt_content_tertiary",
};
